import { AppModule as IAppModule, AppModuleBase, Descriptor } from '../../interfaces/appmodule';
import { IconLoader, IIcon } from '@loaders/IconLoader';
import { SpaConfiguration } from './SpaConfiguration';
import { LocalizeLabel } from '@localization/helpers';
import { metadataRetrieveMultiple, sendMetadataGetRequest, sendMetadataGetRequestAsync } from '@definitions/MetadataApi';
import { WebsiteDefinition } from '../definitions/WebsiteDefinition';
import { cachedWrapper, IPromiseCache } from '../utilities/MemoryCachingHelpers';
import { IODataResponse } from '@src/app/interfaces/general';

export class AppModule {
    private static _currentAppModule: AppModule;
    private static _allowedAppModules: AppModule[] | null = null;
    private static _appModuleCache: IPromiseCache<AppModule[]> = {};

    private _iconName?: string;
    public name: string;
    public description: string;
    public uniquename: string;
    public appmoduleid: string;
    public icon?: IIcon;
    public descriptor: Descriptor;
    public enableglobalsearch: boolean;
    public searchenabledentities: string[];
    public siteMapXml?: string;

    private constructor(appModule: AppModuleBase) {
        this.name = LocalizeLabel(appModule.displayName);
        this.uniquename = appModule.uniqueName;
        this.appmoduleid = appModule.appModuleId;
        this.description = LocalizeLabel(appModule.description);
        this._iconName = appModule.icon;
        if ((appModule as IAppModule).descriptor !== undefined) {
            this.descriptor = JSON.parse((appModule as IAppModule).descriptor);
            this.siteMapXml = (appModule as IAppModule).siteMapXml;
            this.enableglobalsearch = (appModule as IAppModule).talxis_appmodule_talxis_appmodule_nativeappmoduleid?.[0]?.talxis_enableglobalsearch ?? false;
            this.searchenabledentities = (appModule as IAppModule).talxis_appmodule_talxis_appmodule_nativeappmoduleid?.[0]?.talxis_searchenabledentities?.split(',') ?? [];
        }
    }
    static async getAllAppModules(): Promise<AppModule[]> {
        await cachedWrapper("APP_MODULES", async () => {
            const allowedAppModules: AppModule[] = [];
            const website = WebsiteDefinition.getWebsite();

            let filterQueryString = "";
            if (website?.allowedAppModules) {
                const filterQueryArray = [];
                for (const appModule of website.allowedAppModules) {
                    filterQueryArray.push(`uniquename eq '${appModule}'`);
                }
                filterQueryString = `&$filter=(${filterQueryArray.join(" or ")})`;
            }

            const allLocalizedAppModulesRequest = await sendMetadataGetRequestAsync(`v1.0/talxis_RetrieveAppModules(${website?.allowedAppModules ? `UniqueNames=[${website.allowedAppModules.map(x => `'${x}'`).join(',')}]` : 'RetrieveAll=true'})`);
            const appModules: IODataResponse<AppModuleBase> = await allLocalizedAppModulesRequest.json();
            const availableAppmodules = await Xrm.WebApi.retrieveMultipleRecords("appmodule", `?$select=appmoduleid${filterQueryString}&$orderby=name`);
            for (const entity of appModules.value.filter(x => availableAppmodules.entities.some(y => y["appmoduleid"] === x.appModuleId))) {
                const appModule = new AppModule(entity);
                allowedAppModules.push(appModule);
            }
            this._allowedAppModules = allowedAppModules;
            return this._allowedAppModules;
        }, this._appModuleCache);
        // AppModule relies on IconLoader modifying the reference, so we cannot use raw cahedWrapper because it does cloneDeep
        return this._allowedAppModules;
    };
    static get(): AppModule {
        return this._currentAppModule;
    }
    static async loadAsync(name?: string): Promise<boolean> {
        if (!name) {
            name = window.location.pathname.split('/')[1];
        }
        let appModule = await this._getAppModuleAsync(name);
        if (!appModule) {
            name = localStorage.getItem('appModuleName');
            if (!name) {
                name = SpaConfiguration.get().defaultAppModule;
            }
            if (!name) {
                return false;
            }
            appModule = await this._getAppModuleAsync(name);
            if (!appModule) {
                throw new Error(`App module ${name} could not be loaded!`);
            }
        }
        this._currentAppModule = new AppModule(appModule);
        localStorage.setItem('appModuleName', this._currentAppModule.uniquename);
        await this._currentAppModule.loadIcon();
        return true;
    }
    public async loadIcon(): Promise<void> {
        this.icon = await IconLoader.getAsync(`/WebResource/${this._iconName}`);
    }
    private static async _getAppModuleAsync(name?: string): Promise<IAppModule> {
        if (!name) {
            return undefined;
        }
        const response = await sendMetadataGetRequest(`v1.0/talxis_RetrieveAppModule(UniqueName='${name}')`);
        if (!response.ok) {
            console.log(`App module ${name} was not found!`);
            return undefined;
        }
        return response.json();
    }
}